import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin, Observable, Observer, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take, takeUntil} from 'rxjs/operators';
import {LookupValue} from '../../../../../types/lookup-value.enum';
import {Common} from '../../../../../../common/util/Util';
import {CaseHospitalDTO} from '../../../../../models/case-hospital/CaseHospitalDTO';
import {ClmRequestBuilder} from '../../../../../services/clm-request-builder';
import {CaseHospitalProviderLinkDTO} from '../../../../../models/case-hospital/CaseHospitalProviderLinkDTO';
import {ConfirmationDialogComponent} from '../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {DateUtil} from '../../../../../../common/util/DateUtil';
import {NameValueDTO} from '../../../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../../../common/services/lookup.service';
import {ProviderService} from '../../../../../../ent/services/provider.service';
import {ProviderDTO} from '../../../../../../ent/models/ProviderDTO';
import {SharedService} from '../../../../../../common/services/shared-service';
import {PagedResponseDTO} from '../../../../../../common/models/PagedResponseDTO';
import {ContactArrayDTO} from '../../../../../../ent/models/ContactsArrayDTO';
import {AddressesArrayDTO} from '../../../../../../ent/models/AddressesArrayDTO';
import {GenericListDialogComponent} from '../../../../../../common/dialogs/generic-list-dialog/generic-list-dialog.component';
import {getProviderDisplayString} from 'src/app/modules/clm/common/functions/clm.functions';
import {FilterDataService} from 'src/app/modules/common/services/filter-data.service';
import {EntityTable} from 'src/app/modules/common/types/entity-table.enum';

enum Controls {
  PROVIDER_ROLE = 'providerRole',
  PROVIDER_NAME = 'providerName',
  START_DATE = 'startDate',
}

@Component({
  selector: 'app-case-providers-step',
  templateUrl: './case-providers-step.component.html',
  styleUrls: [
    '../../../../../../../../styles/global/_add-view-edit-styles.scss',
    './case-providers-step.component.scss'
  ]
})
export class CaseProvidersStepComponent implements OnChanges, OnInit, OnDestroy {
  readonly controls = Controls;
  readonly dateFormat = DateUtil.dateFormat;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly lookupValue = LookupValue;
  readonly destroyed$ = new Subject();
  readonly byNameOrByBusinessLicenseNumberTextChanged$ = new Subject<string>();

  @Input() data: CaseHospitalDTO;
  @Input() formGroup: FormGroup;
  @Input() isEditMode: boolean;

  // Note: (Hack)
  // Notify to parent when "caseHospitalProviders" doesn't contain at-least 1 entry
  // therefore don't allow parent to navigate until form is valid
  @Output() isValid: EventEmitter<boolean> = new EventEmitter();

  selectedProvider: any;
  filteredProviders: ProviderDTO[];
  providerRolesLookUpValues: NameValueDTO[];
  caseHospitalProviders: CaseHospitalProviderLinkDTO[] = [];
  isCaseHospitalProviderEntryExists: boolean;

  private loggedInUserName: string;

  constructor(
    public datePipe: DatePipe,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private lookupService: LookupService,
    private providerService: ProviderService,
    private sharedService: SharedService,
    private filterDataService: FilterDataService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isEditMode) {
      this.isEditMode = changes.isEditMode.currentValue;
    }
    if (changes.formGroup) {
      this.formGroup = changes.formGroup.currentValue;
    }
    if (changes.data && changes.data.currentValue) {
      this.data = changes.data.currentValue;
      if (this.isEditMode) {
        this.setFormsForEdit(this.data);
      }
    }
  }

  ngOnInit(): void {
    this.initLookUpValues();
    this.initProvidersStepFormControls();
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initLookUpValues() {
    forkJoin(
      this.lookupService.getListOfLookupValues(this.lookupValue.ProviderRole)
    )
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          if (response[0]) {
            this.providerRolesLookUpValues = response[0];
          }
        },
        (error) => {
          console.log(error);
        }
      );
  }

  initProvidersStepFormControls() {
    this.formGroup = this.formBuilder.group({
      [this.controls.PROVIDER_ROLE]: new FormControl(null, !this.isEditMode ? [Validators.required] : []),
      [this.controls.PROVIDER_NAME]: new FormControl(null, !this.isEditMode ? [Validators.required] : []),
      [this.controls.START_DATE]: new FormControl(new Date(), !this.isEditMode ? [Validators.required] : [])
    });
  }

  initSubscriptions() {
    this.sharedService
        .getLoggedInProfile$()
        .pipe(take(1))
        .subscribe(() => {
          // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
          this.loggedInUserName = this.sharedService.getLoggedInUserName();
        });

    this.byNameOrByBusinessLicenseNumberTextChanged$
        .pipe(
          takeUntil(this.destroyed$),
          distinctUntilChanged(),
          debounceTime(this.debounceTime)
        )
        .subscribe({
          next: (value: string) => this.getFilteredListOfProviders(value),
          error: (error) => console.log(error)
        });
  }

  onProviderSelected(provider: ProviderDTO) {
    this.selectedProvider = provider;
  }

  getProviderDisplayString(provider: ProviderDTO): string {
    return getProviderDisplayString(provider);
  }

  onAddProvider() {
    const role = this.formGroup.controls[Controls.PROVIDER_ROLE].value;
    const name = this.formGroup.controls[Controls.PROVIDER_NAME].value;
    const startDate = this.formGroup.controls[Controls.START_DATE].value;

    if (!role || !name || !startDate) {
      this.sharedService.openSnackBar(
        'Please ensure a role, provider and start date have been selected',
        'Dismiss'
      );
    }
    else {
      const newItem = this.getCaseHospitalProviderLinkDTOPayload();
      this.caseHospitalProviders.push(newItem);
      this.patchDefaultFormValue();
      this.setIsCaseHospitalProviderEntryExists();

      // Notify parent form is valid
      this.checkAndNotifyParentIfFormIsValid();
    }
  }

  showSelectedProviderDialog(provider: ProviderDTO): void {
    const COLUMNS_NAMES = 'Facility Type;City/Town;Title;Contact Person;Contact Number;Start - End Dates;Status;Network';
    const columns: string[] = [COLUMNS_NAMES];

    if (provider) {
      this.getProviderById(provider.providerId)
          .pipe(
            take(1)
          )
          .subscribe((providerDetails: ProviderDTO) => {
            const heading = providerDetails.name;
            columns.push(this.createDisplayStringGenericListDialog(providerDetails));
            this.dialog.open(GenericListDialogComponent, {
              width: '50vw',
              data: {list: columns, heading}
            });
          });
    }
  }

  onDeleteProvider(
    $event: MouseEvent,
    caseHospitalProvider: CaseHospitalProviderLinkDTO
  ) {
    $event.stopPropagation();

    const providerName = `${caseHospitalProvider.provider.name}`;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you want to delete ${providerName}?`
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteProviderConfirmed(caseHospitalProvider);
        }
        else {
          dialogRef.close();
        }
      });
  }

  resetState() {
    this.resetSelectedProvider();
    this.resetFilteredProviders();
  }

  getPayload() {
    return {
      caseHospitalProviders: this.caseHospitalProviders
    };
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onBlur($event) {
    $event.preventDefault();
    if (
      this.filteredProviders &&
      this.filteredProviders.length > 0
    ) {
      const value = this.formGroup.controls[Controls.PROVIDER_NAME].value;
      const isMatched = this.filteredProviders.find((item: ProviderDTO) => value.trim() === this.getProviderDisplayString(item));
      if (!isMatched) {
        this.formGroup.patchValue({[this.controls.PROVIDER_NAME]: null});
      }
    }
    else {
      this.formGroup.patchValue({[this.controls.PROVIDER_NAME]: null});
    }
  }

  private onDeleteProviderConfirmed(
    caseHospitalProvider: CaseHospitalProviderLinkDTO
  ) {
    const index = this.caseHospitalProviders.findIndex(
      (item: CaseHospitalProviderLinkDTO) => {
        return (
          item.provider.providerId === caseHospitalProvider.provider.providerId
        );
      }
    );

    if (index > -1) {
      this.caseHospitalProviders.splice(index, 1);
      this.setIsCaseHospitalProviderEntryExists();
      this.checkAndNotifyParentIfFormIsValid();
    }
  }

  private createDisplayStringGenericListDialog(provider: ProviderDTO) {
    let contactsList: ContactArrayDTO[] = [];
    let addressesList: AddressesArrayDTO[] = [];

    if (provider.contacts && provider.contacts.length > 0) {
      contactsList = this.sortByColumn(provider.contacts, 'startDate');
    }
    if (provider.addresses && provider.addresses.length > 0) {
      addressesList = provider.addresses;
    }

    let title;
    let city;
    let contactPerson;
    let contactNumber;

    if (contactsList && contactsList.length > 0) {
      title = `${contactsList[0].contact?.title.name}`;
      contactNumber = `${contactsList[0].contact?.mobileNumber}`;
      contactPerson = `${contactsList[0].contact?.firstName} ${contactsList[0].contact?.lastName}`;
    }
    else {
      title = null;
      contactNumber = null;
      contactPerson = null;
    }

    if (addressesList && addressesList.length > 0) {
      city = provider.addresses[0].address.cityTown;
    }
    else {
      city = null;
    }

    return `${provider.providerFacilityType ? provider.providerFacilityType.name : 'No details available'};
            ${city ? city : 'No details available'};
            ${title ? title : 'No details available'};
            ${contactPerson ? contactPerson : 'No details available'};
            ${contactNumber ? contactNumber : 'No details available'};
            ${this.createDateRangeString(provider.startDate, provider.endDate)};
            ${provider.entityStatus ? provider.entityStatus.name : 'No details available'};
            ${provider.providerNetworkList ? provider.providerNetworkList : 'No details available'}`;
  }

  private createDateRangeString(startDate: Date, endDate: Date): string {
    return `${this.datePipe.transform(startDate, this.dateFormat)} - ${endDate != null ? this.datePipe.transform(endDate, this.dateFormat) : 'current'} `;
  }

  private sortByColumn(list, columnName: string) {
    return list.sort((firstElement, secondElement) => {
      return (
        new Date(secondElement[columnName]).getTime() -
        new Date(firstElement[columnName]).getTime()
      );
    });
  }

  private resetSelectedProvider() {
    this.selectedProvider = null;
  }

  private resetFilteredProviders() {
    this.filteredProviders = null;
  }

  private getCaseHospitalProviderLinkDTOPayload() {
    const caseHospitalProviderLinkDTO = new CaseHospitalProviderLinkDTO();
    caseHospitalProviderLinkDTO.created = new Date();
    caseHospitalProviderLinkDTO.createdBy = this.loggedInUserName;
    caseHospitalProviderLinkDTO.updated = null;
    caseHospitalProviderLinkDTO.updatedBy = null;
    caseHospitalProviderLinkDTO.tenantId = null;

    const role = this.formGroup.controls[Controls.PROVIDER_ROLE].value;
    const startDate = this.formGroup.controls[Controls.START_DATE].value;
    caseHospitalProviderLinkDTO.provider = this.selectedProvider;
    caseHospitalProviderLinkDTO.providerRole =
      this.providerRolesLookUpValues.find(
        (item: NameValueDTO) => item.value === role
      );
    caseHospitalProviderLinkDTO.startDate = startDate
      ? new Date(startDate)
      : null;
    caseHospitalProviderLinkDTO.endDate = null;
    return caseHospitalProviderLinkDTO;
  }

  private getFilteredListOfProviders(value: string): void {
    const basicPagedRequestDTO = ClmRequestBuilder.buildBasicPagedRequestDTO(EntityTable.TblProvider);
    const pagedRequestDTO = this.filterDataService.addFieldMapValues(
        basicPagedRequestDTO, ['name', 'businessLicenseNumber', 'practiceNumber'], value
      );

    this.providerService.getListOfProviders(pagedRequestDTO)
      .subscribe({
        next: (response: PagedResponseDTO<ProviderDTO>) => this.filteredProviders = response.resultList,
        error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  private setFormsForEdit(data: CaseHospitalDTO) {
    const {caseHospitalProviders} = data;
    this.caseHospitalProviders = caseHospitalProviders || [];
    this.setIsCaseHospitalProviderEntryExists();
    this.checkAndNotifyParentIfFormIsValid();
  }

  private patchDefaultFormValue() {
    this.formGroup.patchValue({
      [this.controls.PROVIDER_ROLE]: null,
      [this.controls.PROVIDER_NAME]: null,
      [this.controls.START_DATE]: new Date()
    });
  }

  private setIsCaseHospitalProviderEntryExists() {
    this.isCaseHospitalProviderEntryExists =
      this.isCaseHospitalProvidersExist();
  }

  private isCaseHospitalProvidersExist() {
    return !!(
      this.caseHospitalProviders && this.caseHospitalProviders.length > 0
    );
  }

  private checkAndNotifyParentIfFormIsValid() {
    if (this.isCaseHospitalProviderEntryExists) {
      this.isValid.next(true);
    }
    else {
      this.isValid.next(false);
    }
  }

  private getProviderById(providerId: number): Observable<ProviderDTO> {
    return new Observable((observer: Observer<any>) => {
      this.providerService.getProvider(providerId)
          .pipe(
            take(1),
          )
          .subscribe(
            (response: ProviderDTO) => {
              observer.next(response);
            },
            (errorResponse) => {
              observer.next(false);
              const error = errorResponse.error;
              this.sharedService.openErrorDialog(error.businessMessage);
            }
          );
    });
  }

}
